import styled from "@emotion/styled";
import { nanoid } from "nanoid";
import { AnimatePresence, motion, Variants } from "framer-motion";
import React, { useCallback } from "react";
import { atom, useRecoilState } from "recoil";
import CloseIcon from "../../assets/icon/close.svg";
import { Flex } from "../../utils/HelperStyles";

interface ModalAtomValue {
  id: string;
  component: JSX.Element | null;
  title?: string | JSX.Element | null;
}

interface ModalHook {
  /**
   * `pushModal` has a fire-and-forget mode and it also has an awaitable mode.
   *
   * For the fire and forget mode:
   * ```js
   * const modal = useModal();
   * modal.pushModal(<div>Hello</div>);
   * ```
   *
   * For an awaitable interaction:
   * ```js
   * const modal = useModal();
   *
   * const awaitableInteraction =
   *  modal.pushModal(interaction =>
   *   <div onClick={() => interaction("clicked")}>
   *    Click me!
   *   </div>
   *  )
   *s
   * const interactionResult = await awaitableInteraction;
   * console.log(interactionResult); // "clicked"
   * ```
   */
  pushModal: {
    (
      componentOrFunction: JSX.Element,
      title?: string | undefined | JSX.Element
    ): null;
    (
      componentOrFunction: ElementWithPromise,
      title?: string | undefined
    ): Promise<unknown>;
  };
  popModal: () => void;
}

const ModalAtom = atom<ModalAtomValue[]>({
  key: "ModalAtom",
  default: [],
});

type PromiseResolve = (value: unknown) => void;
type ElementWithPromise = (onModalInteraction: PromiseResolve) => JSX.Element;

export function useModal(): ModalHook {
  const [modalAtom, setModalAtom] = useRecoilState(ModalAtom);

  function pushModalSignature(
    componentOrFunction: JSX.Element,
    title?: string | JSX.Element
  ): null;
  function pushModalSignature(
    componentOrFunction: ElementWithPromise,
    title?: string | JSX.Element
  ): Promise<unknown>;
  function pushModalSignature(
    componentOrFunction: JSX.Element | ElementWithPromise,
    title?: string | JSX.Element
  ): null | Promise<unknown> {
    if (typeof componentOrFunction === "function") {
      const componentFunction = componentOrFunction as (
        onModalInteraction: (value: unknown) => void
      ) => JSX.Element;

      let modalResolve: (value: unknown) => void;
      const modalPromise = new Promise((resolve) => {
        modalResolve = resolve;

        const component = componentFunction(modalResolve);
        setModalAtom([
          ...modalAtom,
          {
            id: nanoid(),
            component,
            title,
          },
        ]);
      });

      return modalPromise;
    }

    const component = componentOrFunction as JSX.Element;
    setModalAtom([
      ...modalAtom,
      {
        id: nanoid(),
        component,
        title,
      },
    ]);

    return null;
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pushModal = useCallback(pushModalSignature, [modalAtom]);

  const popModal = useCallback(() => {
    // This is like this because this hook stores the modal stack from before the current is opened,
    // so modalAtom is one modal behind, currentState otherwise is always updated
    setModalAtom((currentState) => {
      const state = [...currentState];
      state.pop();

      return state;
    });
  }, [setModalAtom]);

  return { pushModal, popModal };
}

const Root = styled.div`
  position: fixed;
  top: 0;
  left: 0;

  width: 100vw;
  height: 100vh;

  display: flex;
  justify-content: center;
  align-items: flex-start;

  background-color: #00000066;
  z-index: 9999;
  overflow: auto;
  padding-bottom: 40px;
`;

const ModalBody = styled(motion.div)`
  position: relative;
  width: auto;
  height: auto;
  display: grid;
  grid-template:
    "title title close"
    "comp comp comp";
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 30px auto;
  border-radius: 10px;
  padding: 15px;
  z-index: 10;
  margin-top: 80px;

  @media only screen and (max-width: 800px) {
    width: 100vw;
    height: 100vh;
    overflow-y: scroll;
    margin-top: 0px;
  }

  background-color: ${(props) => props.theme.colors.card.background};
`;

const Component = styled.div`
  grid-area: comp;
`;

const Title = styled.div`
  grid-area: title;
  ${Flex};
  justify-content: flex-start;
  color: ${(props) => props.theme.colors.title};
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.18px;
`;

const CloseIconContainer = styled.div`
  grid-area: close;
  ${Flex};
  justify-content: flex-end;
  padding-right: 5px;
`;

const CloseIconButton = styled.img`
  cursor: pointer;
`;

const ModalBodyVariants: Variants = {
  closed: {
    y: 800,
    opacity: 0,
  },
  open: {
    y: 0,
    opacity: 1,
  },
};

export default function Modal(): JSX.Element {
  const [modalAtom, setModalAtom] = useRecoilState(ModalAtom);

  function handleCloseModal(currentId: string) {
    setModalAtom(modalAtom.filter(({ id }) => currentId !== id));
  }
  return (
    <AnimatePresence>
      {modalAtom.map((modal) => (
        <Root key={modal.id}>
          <ModalBody
            variants={ModalBodyVariants}
            initial="closed"
            animate="open"
            exit="closed"
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseIconContainer>
              <CloseIconButton
                src={CloseIcon}
                alt="closeIcon"
                onClick={() => handleCloseModal(modal.id)}
              />
            </CloseIconContainer>

            <Title>{modal?.title}</Title>
            <Component>{modal.component}</Component>
          </ModalBody>
        </Root>
      ))}
    </AnimatePresence>
  );
}
